import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { BillingStatusView, ViewService } from 'api';
import { AppService } from 'app/app.service';
import {
  ConfirmComponent,
  ConfirmDialogAction,
  ConfirmDialogData,
} from 'common/common/confirm/confirm.component';
import { NotificationService } from 'common/notification/notification.service';
import { toDisplayState } from 'common/notification/billing-notification.util';
import { firstValueFrom } from 'rxjs';

export const BILLING_ACKNOWLEDGEMENT_REQUIRED_HEADER = 'X-Billing-Error-Code';
export const BILLING_ACKNOWLEDGEMENT_REQUIRED_CODE = 'BILLING_ACKNOWLEDGEMENT_REQUIRED';

@Injectable({
  providedIn: 'root',
})
export class BillingStatusService {
  private readonly dialog = inject(MatDialog);
  private readonly translateService = inject(TranslateService);
  private readonly viewService = inject(ViewService);
  private readonly appService = inject(AppService);
  private readonly notificationService = inject(NotificationService);

  public readonly status = signal<BillingStatusView | null>(null);

  private refreshPromise: Promise<BillingStatusView | null> | null = null;
  private refreshRequested = false;

  public async refresh(): Promise<BillingStatusView | null> {
    if (this.getCurrentTenantId() == null) {
      this.status.set(null);
      this.notificationService.clear();
      return null;
    }

    this.refreshRequested = true;
    if (this.refreshPromise != null) {
      return await this.refreshPromise;
    }

    this.refreshPromise = this.refreshQueued()
      .then(async (status) => {
        await this.publishStatus(status);
        return status;
      })
      .catch((error) => {
        this.status.set(null);
        this.notificationService.clear();
        if (
          !(error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403))
        ) {
          console.warn('Could not refresh tenant billing notifications', error);
        }
        return null;
      })
      .finally(() => (this.refreshPromise = null));
    return await this.refreshPromise;
  }

  public async getStatus(): Promise<BillingStatusView> {
    const status = await firstValueFrom(this.viewService.getTenantBillingStatus());
    this.status.set(status);
    return status;
  }

  public async confirmOverageApproval(): Promise<BillingStatusView> {
    const status = await firstValueFrom(this.viewService.confirmTenantBillingOverageApproval());
    this.status.set(status);
    return status;
  }

  public async ensureAcknowledgementForCreation(): Promise<boolean> {
    let status: BillingStatusView;
    try {
      status = await this.getStatus();
    } catch {
      // A failed status request must not block creation; the backend remains authoritative.
      return true;
    }

    if (!status.nextProductRequiresAcknowledgement) {
      await this.publishStatus(status);
      return true;
    }

    return await this.requestApproval(status);
  }

  public async handleCreationAcknowledgementRequired(error: HttpErrorResponse): Promise<boolean> {
    const status = error.error as BillingStatusView | undefined;
    if (status == null) {
      return false;
    }
    this.status.set(status);
    return await this.requestApproval(status);
  }

  public static getBillingErrorCode(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }
    return error.headers.get(BILLING_ACKNOWLEDGEMENT_REQUIRED_HEADER);
  }

  public async requestApproval(status: BillingStatusView): Promise<boolean> {
    const dialogRef = this.dialog.open<ConfirmComponent, ConfirmDialogData, boolean>(
      ConfirmComponent,
      {
        disableClose: true,
        data: {
          confirmDialogAction: ConfirmDialogAction.OK_CANCEL,
          customConfirmHeadline: this.translateService.instant('billing.approval.headline'),
          customConfirmMessage: this.translateService.instant('billing.approval.message', {
            currentProducts: status.currentProducts,
            licensedProductLimit: status.licensedProductLimit,
            overageRateFormatted: this.formatOverageCosts(status),
            approvalThreshold: status.nextApprovalThreshold,
          }),
          customConfirmButtonTitle: this.translateService.instant('billing.approval.button', {
            overageRateFormatted: this.formatOverageCosts(status),
          }),
        },
      },
    );

    const wasConfirmed = (await firstValueFrom(dialogRef.afterClosed())) === true;
    if (!wasConfirmed) {
      return false;
    }

    try {
      const updatedStatus = await this.confirmOverageApproval();
      await this.publishStatus(updatedStatus);
      return true;
    } catch (error) {
      this.showHttpError(error, 'billing.approval.errorTitle');
      return false;
    }
  }

  private async refreshQueued(): Promise<BillingStatusView> {
    let status!: BillingStatusView;
    do {
      // Fetch billing status. If another refresh was requested while I was waiting for the server,
      // fetch it again. Stop only when the latest fetch completed without another refresh request
      // arriving. see also refresh()
      this.refreshRequested = false;
      status = await this.getStatus();
    } while (this.refreshRequested);
    return status;
  }

  private async publishStatus(status: BillingStatusView): Promise<void> {
    const tenantId = this.getCurrentTenantId();
    if (tenantId == null) {
      this.notificationService.clear();
      return;
    }

    if (status.notificationLevel === 'APPROVAL_REQUIRED') {
      const displayState = toDisplayState(status);
      if (this.notificationService.shouldPresent(tenantId, displayState)) {
        this.notificationService.storeDisplayState(tenantId, displayState);
        //remove me
        if (await this.requestApproval(status)) {
          return;
        }
      }
      this.notificationService.clear();
      return;
    }

    if (status.notificationLevel === 'WARNING') {
      this.showWarning('billing.notifications.warning', status, tenantId);
      return;
    }

    if (status.notificationLevel === 'WARNING_ABOVE') {
      this.showWarning('billing.notifications.warning-above', status, tenantId);
      return;
    }

    this.notificationService.clear();
  }

  private showWarning(
    warningMessageKey: string,
    status: BillingStatusView,
    tenantId: string,
  ): void {
    const message = {
      key: warningMessageKey,
      params: {
        currentProducts: status.currentProducts,
        licensedProductLimit: status.licensedProductLimit,
        approvalThreshold: status.nextApprovalThreshold,
        overageRateFormatted: this.formatOverageCosts(status),
      },
    };
    this.notificationService.setNotifications([message]);
    const displayState = toDisplayState(status);
    if (this.notificationService.shouldPresent(tenantId, displayState)) {
      this.notificationService.storeDisplayState(tenantId, displayState);
      this.notificationService.setPreview([message]);
    }
  }

  private getCurrentTenantId(): string | null {
    return this.appService.adminTenantId() ?? this.appService.user()?.org ?? null;
  }

  private showHttpError(error: unknown, titleMessageKey: string): void {
    if (error instanceof HttpErrorResponse) {
      this.dialog.open<ConfirmComponent, ConfirmDialogData, boolean>(ConfirmComponent, {
        data: {
          confirmDialogAction: ConfirmDialogAction.OK,
          customConfirmMessage: this.translateService.instant('general.httpError', {
            code: error.status,
            message: error.message,
          }),
          customConfirmHeadlineKey: titleMessageKey,
        },
      });
    }
  }

  private formatOverageCosts(status: BillingStatusView): string {
    return Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: status.currencyCode,
    }).format(status.overageRate);
  }
}
